import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useAppContext } from "@/providers";
import { getEnablerInvitation, updateEnablerStatus } from "@/lib/api/initiatives";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { InitiativeEnabler } from "@/types/initiative";

export default function InviteEnablerPage() {
  const { id } = useParams<{ id: string }>();
  const { locale } = useAppContext();
  const isRTL = locale === "ar";

  const [invitation, setInvitation] = useState<InitiativeEnabler | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<"accepted" | "declined" | null>(null);
  const [done, setDone] = useState<"accepted" | "declined" | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getEnablerInvitation(id)
      .then((res) => {
        if (res.success && res.data) {
          setInvitation(res.data);
        } else {
          setError(res.message ?? (locale === "ar" ? "حدث خطأ" : "An error occurred"));
        }
      })
      .catch(() => {
        setError(locale === "ar" ? "حدث خطأ" : "An error occurred");
      })
      .finally(() => setLoading(false));
  }, [id, locale]);

  const handleAction = async (status: "accepted" | "declined") => {
    if (!invitation) return;
    setActionLoading(status);
    try {
      const res = await updateEnablerStatus(invitation.initiative_id, invitation.id, { status });
      if (res.success) {
        setDone(status);
        setInvitation((prev) => (prev ? { ...prev, status } : prev));
      } else {
        setError(res.message ?? (locale === "ar" ? "حدث خطأ" : "An error occurred"));
      }
    } catch {
      setError(locale === "ar" ? "حدث خطأ" : "An error occurred");
    } finally {
      setActionLoading(null);
    }
  };

  const displayName = invitation?.enabler?.name ?? invitation?.invite_email ?? "—";

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "accepted": return "success" as const;
      case "declined": return "error" as const;
      default: return "warning" as const;
    }
  };

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="min-h-screen bg-background flex items-center justify-center p-4"
    >
      <div className="w-full max-w-md bg-card rounded-lg shadow-lg border border-border p-8 relative overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary via-secondary to-brand-green" />

        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
            <span className="material-icons-outlined text-primary text-3xl">person_add</span>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" label={locale === "ar" ? "جارٍ التحميل..." : "Loading..."} />
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-8">
            <span className="material-icons-outlined text-error text-4xl mb-3 block">error_outline</span>
            <p className="text-foreground-secondary">{error}</p>
          </div>
        )}

        {!loading && !error && invitation && (
          <>
            <h1 className="text-xl font-bold text-foreground mb-2 text-center">
              {locale === "ar" ? "دعوة للمشاركة" : "Initiative Invitation"}
            </h1>
            <p className="text-foreground-secondary text-sm mb-6 text-center">
              {locale === "ar"
                ? "لقد تلقيت دعوة للمشاركة كمُمكِّن في مبادرة"
                : "You have been invited to participate as an enabler"}
            </p>

            <div className="bg-muted rounded-lg p-5 space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-sm text-foreground-secondary">
                  {locale === "ar" ? "الاسم" : "Name"}
                </span>
                <span className="text-sm font-semibold text-foreground">{displayName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-foreground-secondary">
                  {locale === "ar" ? "الميزانية المخصصة" : "Allocated Budget"}
                </span>
                <span className="text-sm font-semibold text-foreground">
                  {invitation.budget.toLocaleString()} ﷼
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-foreground-secondary">
                  {locale === "ar" ? "الحالة" : "Status"}
                </span>
                <Badge variant={getStatusVariant(invitation.status)}>
                  {invitation.status === "accepted"
                    ? locale === "ar" ? "مقبول" : "Accepted"
                    : invitation.status === "declined"
                      ? locale === "ar" ? "مرفوض" : "Declined"
                      : locale === "ar" ? "قيد الانتظار" : "Pending"}
                </Badge>
              </div>
            </div>

            {done ? (
              <div
                className={cn(
                  "text-center py-4",
                  done === "accepted" ? "text-success" : "text-foreground-secondary"
                )}
              >
                <span className="material-icons-outlined text-3xl mb-2 block">
                  {done === "accepted" ? "check_circle" : "cancel"}
                </span>
                <p className="text-sm font-medium">
                  {done === "accepted"
                    ? locale === "ar" ? "تم قبول الدعوة بنجاح" : "Invitation accepted successfully"
                    : locale === "ar" ? "تم رفض الدعوة" : "Invitation declined"}
                </p>
              </div>
            ) : invitation.status === "pending" ? (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleAction("declined")}
                  disabled={actionLoading !== null}
                  isLoading={actionLoading === "declined"}
                >
                  {locale === "ar" ? "رفض" : "Decline"}
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => handleAction("accepted")}
                  disabled={actionLoading !== null}
                  isLoading={actionLoading === "accepted"}
                >
                  {locale === "ar" ? "قبول" : "Accept"}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
